/**
 * What a Splitwise category label means, independent of the export language.
 * The English table answers first; anything else comes from a resolution map
 * (server alias or model) or the parser's per-label payment guess.
 */
import type { ResolveSplitwiseLabelsResponseDto } from '@splitfiesta/shared';

import { EXPENSE_TAXONOMY_VERSION } from '../categoryPrediction/taxonomy';
import {
  formatTaxonomyPath,
  isMappedSplitwiseCategory,
  mapSplitwiseCategory,
} from './splitwiseCategories';
import type { SplitwiseFile } from './splitwiseCsv';

export type LabelResolutionSource = 'table' | 'alias' | 'llm' | 'guess';

export type LabelResolution =
  | { kind: 'payment'; source: LabelResolutionSource }
  | { kind: 'category'; path: string; source: LabelResolutionSource };

/** Keyed by `normalizeLabel(label)`. */
export type LabelResolutions = Record<string, LabelResolution>;

const ENGLISH_PAYMENT_LABEL = 'payment';
/** The resolution endpoint's per-label limit. */
const MAX_LABEL_LENGTH = 64;
/** The resolution endpoint's per-request limit; a longer list is a 400. */
export const LABELS_REQUEST_MAX = 100;

export function normalizeLabel(raw: string): string {
  return raw.trim().normalize('NFC').toLowerCase().replace(/\s+/g, ' ');
}

/** English table + the English payment word, source 'table'; null when unknown. */
export function tableResolution(raw: string): LabelResolution | null {
  const label = normalizeLabel(raw);
  if (label === ENGLISH_PAYMENT_LABEL) {
    return { kind: 'payment', source: 'table' };
  }
  if (!isMappedSplitwiseCategory(label)) return null;
  return {
    kind: 'category',
    path: formatTaxonomyPath(mapSplitwiseCategory(label)),
    source: 'table',
  };
}

/**
 * Distinct normalized labels of rows that may be imported (a no-balance-effect
 * row can still be given a payer) that `tableResolution` does not know,
 * first-seen order, limited to what the resolution endpoint accepts.
 */
export function unknownLabels(file: SplitwiseFile): string[] {
  const labels = new Set<string>();
  for (const row of file.rows) {
    if (labels.size === LABELS_REQUEST_MAX) break;
    if (row.kind === 'skipped' && row.skipReason !== 'no-balance-effect') {
      continue;
    }
    const label = normalizeLabel(row.category);
    if (label === '' || label.length > MAX_LABEL_LENGTH) continue;
    if (!labels.has(label) && tableResolution(label) === null) {
      labels.add(label);
    }
  }
  return [...labels];
}

/**
 * The endpoint's answer as a parser resolution map. An answer made for another
 * taxonomy version may name paths this client does not have, so it is dropped
 * whole and the parser's own fallback stands.
 */
export function resolutionsFromDto(
  dto: ResolveSplitwiseLabelsResponseDto
): LabelResolutions {
  const resolutions: LabelResolutions = {};
  if (dto.taxonomyVersion !== EXPENSE_TAXONOMY_VERSION) return resolutions;
  for (const { label, resolution } of dto.labels) {
    if (!resolution) continue;
    // defineProperty, not assignment: a label spelled `__proto__` would
    // otherwise replace the map's prototype instead of adding a key.
    Object.defineProperty(resolutions, normalizeLabel(label), {
      value: resolution,
      enumerable: true,
      writable: true,
      configurable: true,
    });
  }
  return resolutions;
}
